import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import "../../css/header.scss";
import { setAPIAuthorizationToken } from "../../apis/API";
import { get_unread_count } from "../../apis/GetUnreadCount";
import { get_messages_list } from "../../apis/GetMessagesList";

const Header = ({ onMenuToggle, isSidebarCollapsed }) => {
  const [showModal, setShowModal] = useState(false);
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const navigate = useNavigate();
  const messagePopupRef = useRef(null);
  const userDropdownRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentMessages, setRecentMessages] = useState([]);

  // 初始化token
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      setAPIAuthorizationToken(token);
    }
  }, []);

  // 获取当前用户信息
  useEffect(() => {
    const userCookie = Cookies.get("user");
    if (userCookie) {
      try {
        const user = JSON.parse(userCookie);
        setCurrentUser(user);
      } catch (error) {
        console.error("解析用户信息失败:", error);
      }
    }
  }, []);

  // 获取未读消息数量
  const fetchUnreadCount = async () => {
    try {
      const response = await get_unread_count();
      if (response.data && response.data.code === 200) {
        const data = response.data.data || {};
        // 兼容多种返回：data.total_unread / data.unread_count / total_unread / unread_count
        const raw =
          (data.total_unread !== undefined ? data.total_unread : undefined) ??
          (data.unread_count !== undefined ? data.unread_count : undefined) ??
          (response.data.total_unread !== undefined
            ? response.data.total_unread
            : undefined) ??
          (response.data.unread_count !== undefined
            ? response.data.unread_count
            : undefined);
        const count = Number(raw);
        setUnreadCount(Number.isFinite(count) && count > 0 ? count : 0);
      }
    } catch (error) {
      console.error("获取未读消息数量失败:", error);
    }
  };

  // 获取最近的未读消息列表（用于弹窗显示）
  const fetchRecentMessages = async () => {
    try {
      const response = await get_messages_list({
        is_read: "0", // 只获取未读消息
        page: 1,
        page_size: 3, // 只显示最近3条
      });
      if (response.data && response.data.code === 200) {
        const data = response.data.data || {};
        const list = data.list || [];
        setRecentMessages(list);
        const rawFromData =
          (data.total_unread !== undefined ? data.total_unread : undefined) ??
          (data.unread_count !== undefined ? data.unread_count : undefined) ??
          (data?.pagination?.total_unread !== undefined
            ? data.pagination.total_unread
            : undefined);
        const derived = Number(rawFromData);
        const fallback = Number(list?.length || 0);
        const finalVal = Number.isFinite(derived) && derived >= 0 ? derived : fallback;
        setUnreadCount((prev) => (finalVal > prev ? finalVal : prev));
      }
    } catch (error) {
      console.error("获取消息列表失败:", error);
    }
  };

  // 初始化时获取未读消息数量
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      fetchUnreadCount();
    }
  }, []);

  // 监听全局事件，实时刷新未读数量
  useEffect(() => {
    const handler = () => {
      fetchUnreadCount();
    };
    window.addEventListener("refresh-unread-count", handler);
    return () => {
      window.removeEventListener("refresh-unread-count", handler);
    };
  }, []);

  // 定时刷新未读消息数量（每30秒）
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      const interval = setInterval(() => {
        fetchUnreadCount();
      }, 30000); // 30秒

      return () => clearInterval(interval);
    }
  }, []);

  // 处理未授权访问
  const handleShowModal = () => {
    setShowModal(true);
  };

  // 关闭模态框
  const handleCloseShowModal = () => {
    setShowModal(false);
  };

  // 显示/隐藏消息弹出框
  const toggleMessagePopup = async () => {
    const newState = !showMessagePopup;
    setShowMessagePopup(newState);
    // 打开弹窗时获取最近消息
    if (newState) {
      await fetchRecentMessages();
      // 同步刷新未读数量，确保角标及时更新
      await fetchUnreadCount();
    }
  };

  // 跳转到消息列表页面
  const goToMessageList = () => {
    setShowMessagePopup(false);
    navigate("/message-subscription");
  };

  // 格式化时间显示
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return "";
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diff = now - messageTime;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) {
      return `${minutes}分钟前`;
    } else if (hours < 24) {
      return `${hours}小时前`;
    } else if (days < 30) {
      return `${days}天前`;
    } else {
      return messageTime.toLocaleDateString("zh-CN");
    }
  };

  // 获取消息类型的中文名称
  const getMessageTypeName = (type) => {
    const typeMap = {
      system: "系统通知",
      account: "账户消息",
      transaction: "交易提醒",
      announcement: "公告通知",
    };
    return typeMap[type] || "通知";
  };

  // 显示/隐藏用户下拉菜单
  const toggleUserDropdown = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  // 跳转到用户中心
  const goToUserCenter = () => {
    setShowUserDropdown(false);
    navigate("/user-center");
  };

  // 退出登录
  const handleLogout = () => {
    setShowUserDropdown(false);
    // 清除登录状态
    Cookies.remove("token");
    Cookies.remove("user");
    navigate("/");
  };

  // 点击外部关闭消息弹出框和用户下拉菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        messagePopupRef.current &&
        !messagePopupRef.current.contains(event.target)
      ) {
        setShowMessagePopup(false);
      }
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target)
      ) {
        setShowUserDropdown(false);
      }
    };

    if (showMessagePopup || showUserDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMessagePopup, showUserDropdown]);

  return (
    <>
      <header className="header-container">
        <div className={`header-left ${isSidebarCollapsed ? "collapsed" : ""}`}>
          <div className="logo-section">
            <div className="logo-icon">
              <img alt="" src="/head/header1.svg" />
            </div>
            <span className={`logo-text ${isSidebarCollapsed ? "hidden" : ""}`}>
              Global E-Payment
            </span>
          </div>
          <button className="menu-btn" onClick={onMenuToggle}>
            <img alt="" src="/head/header2.png" />
          </button>
        </div>

        <div className="header-main">
          <div className="header-center">
            <span className="news-badge">
              <img alt="" src="/head/header3.png" />
            </span>
            <div className="news-marquee">
              <span className="news-text">
                【品牌升级公告】2025-10-17
                关于暂停电商收款TikTok（泰国站）银行账户... 2025-03-20
              </span>
            </div>
          </div>

          <div className="header-right">
            <div className="header-icon-item">
              <img alt="" src="/head/header4-1.svg" />
              <span onClick={handleShowModal}>我的优惠</span>
            </div>
            <div
              className="header-icon-item message-item"
              onClick={toggleMessagePopup}
              ref={messagePopupRef}
            >
              <div className="icon-with-badge">
                <img alt="" src="/head/header4-2.png" />
                {unreadCount > 0 && (
                  <span className="badge">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </div>
              <span>消息</span>

              {/* 消息弹出框 */}
              {showMessagePopup && (
                <div
                  className="message-popup"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="popup-header">
                    <span className="popup-title">消息通知</span>
                    <button className="view-all-btn" onClick={goToMessageList}>
                      查看全部
                    </button>
                  </div>

                  <div className="popup-content">
                    {recentMessages.length > 0 ? (
                      recentMessages.map((message) => (
                        <div
                          key={message.id}
                          className="message-item-popup"
                          onClick={() => {
                            // 可选：点击消息项时标记为已读并跳转
                            navigate("/message-subscription");
                            setShowMessagePopup(false);
                          }}
                        >
                          <div className="message-icon">
                            <img alt="" src="/head/header3.png" />
                          </div>
                          <div className="message-info">
                            <div className="message-title-popup">
                              {getMessageTypeName(message.type)}
                            </div>
                            <div className="message-desc">{message.title}</div>
                            <div className="message-time">
                              {formatMessageTime(message.created_at)}
                            </div>
                          </div>
                          {message.is_read === 0 && (
                            <span className="unread-dot"></span>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="empty-tip">暂无未读消息</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="header-icon-item">
              <img alt="" src="/main/login3.png" />
              <span onClick={handleShowModal}>帮助中心</span>
            </div>
            <div
              className="user-section"
              onClick={toggleUserDropdown}
              ref={userDropdownRef}
            >
              <div className="user-avatar">
                <img alt="" src="/head/header4-4.png" />
              </div>
              <span className="user-name">
                {currentUser?.username || currentUser?.email || "用户"}
              </span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path
                  d="M4 6l4 4 4-4"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>

              {/* 用户下拉菜单 */}
              {showUserDropdown && (
                <div
                  className="user-dropdown"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="dropdown-header">
                    <div className="dropdown-company-name">
                      {currentUser?.username || currentUser?.email || "用户"}
                    </div>
                  </div>

                  <div className="dropdown-divider"></div>

                  <div className="dropdown-menu">
                    <div
                      className="dropdown-menu-item"
                      onClick={goToUserCenter}
                    >
                      用户中心
                    </div>
                  </div>

                  <div className="dropdown-divider"></div>

                  <div className="dropdown-footer">
                    <div
                      className="dropdown-menu-item logout"
                      onClick={handleLogout}
                    >
                      退出登录
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                      >
                        <path
                          d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3M11 11l3-3-3-3M14 8H6"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          fill="none"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* 未授权访问模态框 */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseShowModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon">🔒</div>
            <h2 className="modal-title">未授权访问</h2>
            <p className="modal-message">您当前没有权限访问此功能</p>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
